import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { fetchIbkrFlex } from "../../api/client";
import type { Portfolio } from "../../api/client";

interface Props {
  portfolios: Portfolio[];
  selectedPortfolioId: number | null;
  onSelectPortfolio: (id: number) => void;
}

export default function Layout({
  portfolios,
  selectedPortfolioId,
  onSelectPortfolio,
}: Props) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<"idle" | "ok" | "error">("idle");
  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleIbkrRefresh = async () => {
    if (!selectedPortfolioId || refreshing) return;
    setRefreshing(true);
    setRefreshStatus("idle");
    try {
      await fetchIbkrFlex(selectedPortfolioId);
      setRefreshStatus("ok");
    } catch {
      setRefreshStatus("error");
    } finally {
      setRefreshing(false);
      if (statusTimer.current) clearTimeout(statusTimer.current);
      statusTimer.current = setTimeout(() => setRefreshStatus("idle"), 4000);
    }
  };

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile((prev) => {
        if (prev && !mobile) {
          // Transitioning from mobile → desktop: open sidebar
          setSidebarOpen(true);
        } else if (!prev && mobile) {
          // Transitioning from desktop → mobile: close sidebar
          setSidebarOpen(false);
        }
        return mobile;
      });
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const closeSidebarOnMobile = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 99,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          width: sidebarOpen ? 240 : 0,
          overflow: "hidden",
          background: "#1e293b",
          color: "#f1f5f9",
          transition: "width 0.2s",
          display: "flex",
          flexDirection: "column",
          ...(isMobile && {
            position: "fixed",
            top: 0,
            left: 0,
            height: "100%",
            zIndex: 100,
          }),
        }}
      >
        <div style={{ padding: "20px 16px 8px", fontWeight: 700, fontSize: 18, letterSpacing: "-0.5px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>📈 TradingWeb</span>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}
              aria-label="Close menu"
            >
              ✕
            </button>
          )}
        </div>

        {/* Nav links */}
        <nav style={{ borderTop: "1px solid #334155", padding: "8px 0", flex: 1, overflowY: "auto" }}>
          {[
            { to: "/", label: "📊 Dashboard" },
            { to: "/positions", label: "🗂️ Positions" },
            { to: "/trades", label: "🔄 Trades" },
            { to: "/performance", label: "🏆 Performance" },
            { to: "/benchmark", label: "🎯 Benchmark" },
            { to: "/recommendations", label: "💡 Recommendations" },
            { to: "/chat", label: "🤖 AI Chat" },
            { to: "/market-data", label: "📊 Market Data" },
            { to: "/intraday-evolution", label: "⚡ Intraday Evolution" },
            { to: "/import", label: "⬆️ Data & Intraday" },
            { to: "/portfolio-settings", label: "⚙️ Settings" },
          ].map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={closeSidebarOnMobile}
              style={({ isActive }) => ({
                display: "block",
                padding: "10px 16px",
                color: isActive ? "#fff" : "#94a3b8",
                background: isActive ? "#334155" : "transparent",
                textDecoration: "none",
                fontSize: 14,
              })}
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#f8fafc", minWidth: 0, ...(isMobile && sidebarOpen && { pointerEvents: "none" }) }}>
        <header
          style={{
            padding: "12px 16px",
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 12,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "4px 6px", color: "#334155" }}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <select
            value={selectedPortfolioId ?? ""}
            onChange={(e) => onSelectPortfolio(Number(e.target.value))}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 14,
              fontWeight: 600,
              color: "#1e293b",
              background: "#f8fafc",
              cursor: "pointer",
              maxWidth: 220,
            }}
          >
            {portfolios.length === 0 && (
              <option value="" disabled>No portfolios</option>
            )}
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {/* IBKR Flex refresh */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            {refreshStatus === "ok" && (
              <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 500 }}>✓ Imported</span>
            )}
            {refreshStatus === "error" && (
              <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 500 }}>✗ Failed</span>
            )}
            <button
              onClick={handleIbkrRefresh}
              disabled={refreshing || !selectedPortfolioId}
              title="Fetch latest data from IBKR Flex Web Service"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: refreshing ? "#94a3b8" : "#1e293b",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: refreshing || !selectedPortfolioId ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
            >
              <span style={refreshing ? { display: "inline-block", animation: "spin 1s linear infinite" } : {}}>
                🔄
              </span>
              {refreshing ? "Fetching…" : "IBKR Sync"}
            </button>
          </div>
        </header>

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
        <main style={{ flex: 1, padding: "16px", overflowY: "auto" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
